import React, { useContext, useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import productData from "../Datas/productData";
import Footer from "./Footer";
import "../css/productdetails.scss";
import { CartContext } from "../Component/CartContext";
import Header from "./Header";
import { FiMapPin, FiTruck, FiStar } from "react-icons/fi";

const SIZE_RANGE_MAP = {
  XXS: "(00-0)",
  XS: "(2-4)",
  S: "(4-6)",
  M: "(8-10)",
  L: "(12-14)",
  XL: "(16-18)",
  "2XL": "(20-22)",
};

function formatGbp(price) {
  const n = Number(price);
  if (Number.isNaN(n)) return price;
  return n % 1 === 0 ? String(Math.round(n)) : n.toFixed(0);
}

function formatReviewCount(n) {
  const num = Number(n);
  if (Number.isNaN(num)) return n;
  return num.toLocaleString("en-GB");
}

function getDetailBlocks(product) {
  const descriptionBody =
    product.detailDescription || product.description.join(" ");
  const exploreLabel = product.detailExploreLabel;
  const fitBullets =
    product.fitBullets ||
    (() => {
      const feats = product.features || [];
      const nonCare = feats.filter(
        (f) => !/wash|dry|tumble|Machine|Hand wash/i.test(f)
      );
      if (nonCare.length >= 2) return nonCare.slice(0, 2);
      return [
        `Engineered for ${(
          product.category || "active"
        ).toLowerCase()} wear with averlynxstore ease and movement.`,
        "See Size Guide for UK / EU conversions and measuring tips.",
      ];
    })();
  const fabricationBullets =
    product.fabricationBullets ||
    (() => {
      const care = (product.features || []).filter((f) =>
        /wash|dry|clean|tumble|Hand wash/i.test(f)
      );
      const lines = [product.material, ...care.slice(0, 3)].filter(Boolean);
      return lines.length
        ? lines
        : [product.material, "Follow care label instructions."];
    })();
  const fitBlurb =
    product.fitBlurb ||
    `Standard ${
      product.category || "averlynxstore"
    } fit. Size up or down depending on coverage preference.`;
  return {
    descriptionBody,
    exploreLabel,
    fitBullets,
    fabricationBullets,
    fitBlurb,
  };
}

function Stars({ rating }) {
  const r = Math.min(5, Math.max(0, Number(rating) || 0));
  const full = Math.floor(r);
  const half = r - full >= 0.25 && r - full < 0.75;
  return (
    <span className="product-detail__stars" aria-hidden>
      {[0, 1, 2, 3, 4].map((i) => {
        if (i < full) {
          return (
            <FiStar
              key={i}
              className="product-detail__star product-detail__star--full"
            />
          );
        }
        if (half && i === full) {
          return (
            <span key={i} className="product-detail__star-half-wrap">
              <FiStar className="product-detail__star product-detail__star--full" />
            </span>
          );
        }
        return (
          <FiStar
            key={i}
            className="product-detail__star product-detail__star--empty"
          />
        );
      })}
    </span>
  );
}

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useContext(CartContext);

  const product = useMemo(
    () => productData.find((item) => item.id === parseInt(id, 10)),
    [id]
  );

  const [selectedSize, setSelectedSize] = useState("");
  const [selectedColor, setSelectedColor] = useState("");
  const [selectedImage, setSelectedImage] = useState(0);

  useEffect(() => {
    const p = productData.find((item) => item.id === parseInt(id, 10));
    if (!p) return;
    setSelectedSize(p.size?.[0] || "");
    setSelectedColor(p.colors?.[0] || "");
    setSelectedImage(0);
  }, [id]);

  const toTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  if (!product) {
    return (
      <div className="product-detail">
        <Header />
        <div className="product-detail__not-found">
          <h2>Product not found</h2>
          <Link onClick={toTop} to="/shop">
            Back to Shop
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const images = product.images?.length ? product.images : [product.image];
  const blocks = getDetailBlocks(product);

  const handleAddToCart = () => {
    addToCart(product.id, 1, selectedSize, selectedColor);
    navigate("/cartpage");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="product-detail">
      <Header />

      <main className="product-detail__main">
        <div className="product-detail__layout">
          <div className="product-detail__gallery">
            <div className="product-detail__grid">
              {images.map((src, idx) => (
                <button
                  key={src + idx}
                  type="button"
                  className={`product-detail__cell ${
                    selectedImage === idx ? "is-active" : ""
                  }`}
                  onClick={() => setSelectedImage(idx)}
                >
                  <img src={src} alt="" className="product-detail__grid-img" />
                  {idx === 1 && product.modelNote ? (
                    <span className="product-detail__model-badge">
                      {product.modelNote}
                    </span>
                  ) : null}
                </button>
              ))}
            </div>
          </div>

          <aside className="product-detail__panel">
            <div className="product-detail__panel-inner">
              {product.isBestSeller ? (
                <p className="product-detail__tag">BEST SELLER</p>
              ) : null}
              <h1 className="product-detail__title">{product.name}</h1>

              <div className="product-detail__price-row" id="reviews">
                <span className="product-detail__price">
                  ${formatGbp(product.price)}
                </span>
                <span className="product-detail__sep" aria-hidden>
                  |
                </span>
                <Stars rating={product.rating} />
                <span className="product-detail__reviews">
                  ({formatReviewCount(product.reviews)})
                </span>
              </div>
              <p className="product-detail__tax-note">
                (Including taxes &amp; duties)
              </p>

              {product.colors?.length ? (
                <div className="product-detail__colors">
                  <span className="product-detail__label">Colour:</span>
                  <div className="product-detail__color-btns">
                    {product.colors.map((c) => (
                      <button
                        key={c}
                        type="button"
                        className={`product-detail__color-btn ${
                          selectedColor === c ? "is-active" : ""
                        }`}
                        onClick={() => setSelectedColor(c)}
                      >
                        {c}
                      </button>
                    ))}
                  </div>
                </div>
              ) : null}

              <div className="product-detail__fit-block">
                <p className="product-detail__fit-label">Fit:</p>
                <p className="product-detail__fit-text">{blocks.fitBlurb}</p>
              </div>

              <div className="product-detail__sizes" id="size-guide">
                <div className="product-detail__size-grid">
                  {product.size.map((sz) => {
                    const range = SIZE_RANGE_MAP[sz];
                    return (
                      <button
                        key={sz}
                        type="button"
                        className={`product-detail__size-btn ${
                          selectedSize === sz ? "is-active" : ""
                        }`}
                        onClick={() => setSelectedSize(sz)}
                      >
                        <span className="product-detail__size-code">{sz}</span>
                        {range ? (
                          <span className="product-detail__size-range">
                            {range}
                          </span>
                        ) : null}
                      </button>
                    );
                  })}
                </div>
              </div>

              <button
                type="button"
                className="product-detail__add-bag"
                onClick={handleAddToCart}
              >
                ADD TO BAG
              </button>

              <div className="product-detail__shipping">
                <div className="product-detail__ship-row">
                  <FiMapPin className="product-detail__ship-icon" aria-hidden />
                  <div>
                    <p className="product-detail__ship-title">
                      Buy Online, Pickup in Store
                    </p>
                    <p className="product-detail__ship-sub">
                      Select a size to check store inventory
                    </p>
                  </div>
                </div>
                <div className="product-detail__ship-row">
                  <FiTruck className="product-detail__ship-icon" aria-hidden />
                  <div>
                    <p className="product-detail__ship-title">
                      Free Shipping Over $60 and Free Returns
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </aside>
        </div>

        <section
          className="product-detail__info-block"
          aria-label="Product information"
        >
          <div className="product-detail__info-inner">
            <div className="product-detail__info-col">
              <h2 className="product-detail__info-heading">DESCRIPTION</h2>
              <p className="product-detail__info-text">
                {product.description.join("")}
              </p>
            </div>
            <div className="product-detail__info-col">
              <h2 className="product-detail__info-heading">FIT</h2>
              <ul className="product-detail__info-list">
                {product.features.map((line, i) => (
                  <li key={i}>{line}</li>
                ))}
              </ul>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default ProductDetail;
